import React, { useState, useMemo } from 'react';
import { calculateCashReconciliation } from '../../utils/cashRounding';

const denominations = [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05];

const CloseTillModal = ({ onConfirm, onCancel, openingFloat, openSession }) => {
  const [counts, setCounts] = useState({});
  const [step, setStep] = useState('entry');
  const [closingNote, setClosingNote] = useState('');
  const [leaveAmount, setLeaveAmount] = useState(
    Number.isFinite(Number(openingFloat)) ? Number(openingFloat) : 0
  );

  const sanitizedLeaveAmount = Number.isFinite(Number(leaveAmount)) ? Number(leaveAmount) : 0;

  const total = useMemo(() => {
    return denominations.reduce((acc, den) => {
      return acc + (counts[den] || 0) * den;
    }, 0);
  }, [counts]);

  const cashSales = Number(openSession?.salesSummary?.cash?.total || 0);
  const cashRefunds = Number(openSession?.refundsSummary?.cash?.total || 0);
  const reconciliation = calculateCashReconciliation({
    openingFloat,
    cashSales,
    cashRefunds,
    countedCash: total,
    leaveAmount: sanitizedLeaveAmount,
  });

  const breakdown = useMemo(() => {
    return denominations
      .map((den) => {
        const count = counts[den] || 0;
        return {
          den,
          count,
          subtotal: count * den,
        };
      })
      .filter((entry) => entry.count > 0);
  }, [counts]);

  const handleCountChange = (den, count) => {
    setCounts(prev => ({ ...prev, [den]: count }));
  };

  const handleConfirm = () => {
    onConfirm(total, counts, sanitizedLeaveAmount, closingNote.trim());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start py-8">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Close Till</h2>
        <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          Opening Float: <span className="font-semibold">${Number(openingFloat || 0).toFixed(2)}</span>
        </div>
        {step === 'entry' ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Target Float / Leave Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={Number.isFinite(Number(leaveAmount)) ? leaveAmount : ''}
                onChange={(e) => setLeaveAmount(e.target.value)}
                className="w-full p-2 border rounded-md"
              />
              <p className="text-xs text-slate-500 mt-1">
                Default is the opening float. Bank deposit is rounded down, so the actual float left in the till can be higher than this target.
              </p>
            </div>
            <div className="space-y-4">
              {denominations.map((den) => {
                const count = counts[den] || 0;
                const subtotal = count * den;
                return (
                  <div key={den} className="flex items-center justify-between gap-3">
                    <span className="font-medium w-20">${den.toFixed(2)}</span>
                    <input
                      type="number"
                      value={counts[den] || ''}
                      onChange={(e) => handleCountChange(den, parseInt(e.target.value, 10) || 0)}
                      className="w-24 p-2 border rounded-md"
                    />
                    <span className="text-sm text-slate-600 w-24 text-right">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="text-right font-bold text-xl mt-4">
              Total: ${total.toFixed(2)}
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Review the counted cash before closing the till.
            </p>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Cash Reconciliation</h3>
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <tbody>
                  <tr className="bg-slate-50">
                    <td className="px-3 py-2 text-slate-600">Opening Float</td>
                    <td className="px-3 py-2 text-right font-semibold">${reconciliation.openingFloatAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-slate-600">Cash Sales</td>
                    <td className="px-3 py-2 text-right">${cashSales.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-slate-600">Cash Refunds</td>
                    <td className="px-3 py-2 text-right">${cashRefunds.toFixed(2)}</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-3 py-2 text-slate-600">Expected Cash</td>
                    <td className="px-3 py-2 text-right font-semibold">${reconciliation.expectedCashAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-slate-600">Counted Cash</td>
                    <td className="px-3 py-2 text-right">${reconciliation.countedCashAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-slate-600">Target Float</td>
                    <td className="px-3 py-2 text-right">${reconciliation.targetFloatAmount.toFixed(2)}</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-3 py-2 text-slate-600">Bank Deposit</td>
                    <td className="px-3 py-2 text-right font-semibold">${reconciliation.bankDepositAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-slate-600">Float Left in Till</td>
                    <td className="px-3 py-2 text-right">${reconciliation.actualFloatAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-slate-600">Variance</td>
                    <td className="px-3 py-2 text-right font-semibold">${reconciliation.cashVarianceAmount.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Denomination</th>
                    <th className="px-4 py-2 text-right">Count</th>
                    <th className="px-4 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {breakdown.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-center text-slate-500">
                        No cash counted.
                      </td>
                    </tr>
                  )}
                  {breakdown.map((entry) => (
                    <tr key={entry.den}>
                      <td className="px-4 py-2">${entry.den.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">{entry.count}</td>
                      <td className="px-4 py-2 text-right">${entry.subtotal.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-right font-bold text-xl">
              Total: ${total.toFixed(2)}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Handover Note (optional)
              </label>
              <textarea
                rows={3}
                maxLength={100}
                value={closingNote}
                onChange={(e) => setClosingNote(e.target.value)}
                placeholder="Add a note for next opening (max 100 chars)"
                className="w-full p-2 border rounded-md"
              />
              <p className="text-xs text-slate-500 mt-1 text-right">{closingNote.length}/100</p>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 pt-4">
          <button onClick={onCancel} className="bg-gray-200 px-4 py-2 rounded-md">Cancel</button>
          {step === 'entry' ? (
            <button
              onClick={() => setStep('review')}
              className="bg-primary text-white px-4 py-2 rounded-md"
            >
              Review
            </button>
          ) : (
            <>
              <button
                onClick={() => setStep('entry')}
                className="bg-slate-100 text-slate-700 px-4 py-2 rounded-md"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                className="bg-primary text-white px-4 py-2 rounded-md"
              >
                Confirm & Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloseTillModal;
